'use client'

import { useCallback, useEffect, useState } from 'react'

type DemoOnboardingTourProps = {
  user: {
    id?: number | string
    firm?: Record<string, unknown> | null
    platforms?: Array<string | { platform?: string | null; name?: string | null }> | null
  } | null
  startSignal?: number
}

type TargetRect = {
  top: number
  left: number
  width: number
  height: number
}

type TourStep = {
  target: string
  title: string
  body: string
  before?: 'open-platform-dropdown' | 'close-platform-dropdown' | 'open-search'
  afterNext?: 'finish-open-setup'
}

const PLATFORM_SWITCHER_TARGET = '[data-onboarding-target="platform-switcher"]'
const PLATFORM_DROPDOWN_TARGET = '[data-onboarding-target="platform-dropdown"]'
const PLATFORM_PIN_TARGET = '[data-onboarding-target="platform-pin"]'
const SITE_PREVIEW_TARGET = '[data-onboarding-target="site-preview-link"]'
const TOPBAR_SEARCH_TARGET = '[data-onboarding-target="topbar-search"]'
const SEARCH_MODAL_TARGET = '[data-onboarding-target="search-modal"]'
const SETUP_START_TARGET = '[data-onboarding-target="setup-start"]'
const TOUR_CARD_WIDTH = 344
const TOUR_CARD_HEIGHT = 220
const TOUR_MARGIN = 16
const TOUR_GAP = 14

const PLATFORM_DESCRIPTIONS: Record<string, string> = {
  'customer service': 'Müşteri iletişimi, talepler, destek süreçleri ve çağrı operasyonları bu platformda takip edilir.',
  'depo & mağaza': 'Depo, mağaza, stok hareketleri, toplama ve operasyonel lojistik ekranları burada yer alır.',
  'depo magaza': 'Depo, mağaza, stok hareketleri, toplama ve operasyonel lojistik ekranları burada yer alır.',
  dizayn: 'Vitrin tasarımı, tema, sayfa düzenleri ve görsel yapılandırmalar bu alandan yönetilir.',
  design: 'Vitrin tasarımı, tema, sayfa düzenleri ve görsel yapılandırmalar bu alandan yönetilir.',
  erp: 'ERP tarafında stok, ürün kartları, muhasebe bağlantıları ve dış sistem senkronları yönetilir.',
  lojistik: 'Kargo, sevkiyat, iade, teslimat ve lojistik takip süreçleri bu platformda toplanır.',
  store: 'Mağaza ayarları, satış kanalı yapılandırmaları ve vitrin operasyonları için kullanılır.',
  web: 'Web platformunda e-ticaret operasyonu, ürün, sipariş, kampanya ve müşteri süreçleri yürütülür.',
  farktor: 'Farktör yönetim ve genel panel araçlarını barındırır.',
  report: 'Raporlama, analiz ve performans izleme ekranları bu platformda bulunur.',
  'virtual market': 'Pazar yeri entegrasyonları, ürün aktarımı ve kanal siparişleri bu alandan yönetilir.',
}

const STATIC_TOUR_STEPS: TourStep[] = [
  {
    target: PLATFORM_SWITCHER_TARGET,
    title: 'Platform seçimi',
    body: 'Farktör farklı iş alanlarını platformlara ayırır. Buradan Web, depo, pazar yeri gibi yetkili olduğunuz platformlar arasında geçiş yapabilirsiniz.',
  },
  {
    target: PLATFORM_DROPDOWN_TARGET,
    title: 'Yetkili platformlarınız',
    body: 'Açılan listede yalnızca erişiminiz olan platformlar görünür. Bir platform seçtiğinizde menüler ve sayfa yetkileri ona göre yenilenir.',
    before: 'open-platform-dropdown',
  },
  {
    target: PLATFORM_PIN_TARGET,
    title: 'Platformları sabitleyin',
    body: 'Sabitle butonu platform listesini üstte hızlı geçiş barı olarak tutar. Birden fazla platformla çalışırken geçişleri hızlandırır.',
    before: 'open-platform-dropdown',
  },
  {
    target: SITE_PREVIEW_TARGET,
    title: 'Sitenizi görüntüleyin',
    body: 'Siteye Git bağlantısı mağazanızın canlı adresini yeni sekmede açar. Kurulum sırasında yaptığınız değişiklikleri buradan kontrol edebilirsiniz.',
    before: 'close-platform-dropdown',
  },
  {
    target: TOPBAR_SEARCH_TARGET,
    title: 'Panel araması',
    body: 'Üst bardaki arama alanı; sipariş, ürün, müşteri, yardım ve menülere hızlı ulaşmak için kullanılır.',
  },
  {
    target: SEARCH_MODAL_TARGET,
    title: 'Arama penceresi',
    body: 'Arama penceresinde yazmaya başladığınızda ilgili sayfalar, kayıtlar ve yardım önerileri listelenir.',
    before: 'open-search',
  },
  {
    target: SETUP_START_TARGET,
    title: 'Kuruluma başlayın',
    body: 'Şimdi turu bitirip mağaza kurulum adımlarına geçeceğiz. Sonraki dediğinizde kurulum penceresi açılacak.',
    afterNext: 'finish-open-setup',
  },
]

function normalizePlatformKey(value: string) {
  return value.trim().toLocaleLowerCase('tr-TR').replace(/\s+/g, ' ')
}

function getPlatformDescription(platform: string) {
  return PLATFORM_DESCRIPTIONS[normalizePlatformKey(platform)] || `${platform} platformuna ait yetkili menüler ve günlük işlemler bu alanda listelenir.`
}

function getPlatformValue(platform: string | { platform?: string | null; name?: string | null }) {
  if (typeof platform === 'string') return platform
  return platform.platform || platform.name || ''
}

function getPlatformTourSteps(user: DemoOnboardingTourProps['user']): TourStep[] {
  const userPlatforms = Array.isArray(user?.platforms)
    ? user.platforms.map(getPlatformValue)
    : []
  const domPlatforms = Array.from(document.querySelectorAll<HTMLElement>('[data-onboarding-platform]'))
    .map((element) => element.dataset.onboardingPlatform || element.textContent || '')

  return [...new Set([...userPlatforms, ...domPlatforms])]
    .map((platform) => platform.trim())
    .filter(Boolean)
    .map((platform) => ({
      target: `[data-onboarding-platform="${CSS.escape(platform)}"]`,
      title: `${platform} platformu`,
      body: getPlatformDescription(platform),
      before: 'open-platform-dropdown' as const,
    }))
}

function buildTourSteps(user: DemoOnboardingTourProps['user']) {
  const platformSteps = getPlatformTourSteps(user)
  return [
    ...STATIC_TOUR_STEPS.slice(0, 3),
    ...platformSteps,
    ...STATIC_TOUR_STEPS.slice(3),
  ]
}

export function DemoOnboardingTour({ user, startSignal = 0 }: DemoOnboardingTourProps) {
  const [visible, setVisible] = useState(false)
  const [welcomeVisible, setWelcomeVisible] = useState(false)
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [tourSteps, setTourSteps] = useState<TourStep[]>(STATIC_TOUR_STEPS)
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null)
  const activeStep = tourSteps[activeStepIndex]

  const updateTargetRect = useCallback((selector: string) => {
    const target = document.querySelector<HTMLElement>(selector)
    if (!target) {
      setTargetRect(null)
      return false
    }

    const rect = target.getBoundingClientRect()
    setTargetRect({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    })
    return true
  }, [])

  const isPlatformDropdownOpen = useCallback(() => Boolean(document.querySelector(PLATFORM_DROPDOWN_TARGET)), [])

  const openPlatformDropdown = useCallback(() => {
    if (isPlatformDropdownOpen()) return
    document.querySelector<HTMLElement>(PLATFORM_SWITCHER_TARGET)?.click()
  }, [isPlatformDropdownOpen])

  const closePlatformDropdown = useCallback(() => {
    if (!isPlatformDropdownOpen()) return
    document.querySelector<HTMLElement>(PLATFORM_SWITCHER_TARGET)?.click()
  }, [isPlatformDropdownOpen])

  const openSearchModal = () => {
    if (document.querySelector(SEARCH_MODAL_TARGET)) return
    document.querySelector<HTMLElement>(TOPBAR_SEARCH_TARGET)?.click()
  }

  const closeSearchModal = () => {
    const modal = document.querySelector(SEARCH_MODAL_TARGET)
    if (!modal) return
    document.querySelector<HTMLElement>('.command-search__close')?.click()
  }

  const openSetupGuide = () => {
    window.dispatchEvent(new CustomEvent('panel:setup-guide-open'))
  }

  const runStepBefore = useCallback((step: TourStep) => {
    if (step.before === 'open-platform-dropdown') openPlatformDropdown()
    if (step.before === 'close-platform-dropdown') closePlatformDropdown()
    if (step.before === 'open-search') openSearchModal()
    if (step.target !== SEARCH_MODAL_TARGET) closeSearchModal()
  }, [closePlatformDropdown, openPlatformDropdown])

  useEffect(() => {
    if (!user || startSignal <= 0) return

    const frame = window.requestAnimationFrame(() => {
      closePlatformDropdown()
      setActiveStepIndex(0)
      setTourSteps(STATIC_TOUR_STEPS)
      setTargetRect(null)
      setWelcomeVisible(true)
      setVisible(true)
    })

    return () => window.cancelAnimationFrame(frame)
  }, [closePlatformDropdown, startSignal, user])

  useEffect(() => {
    if (!visible || welcomeVisible || !activeStep) return

    const frame = window.requestAnimationFrame(() => updateTargetRect(activeStep.target))
    const handleViewportChange = () => updateTargetRect(activeStep.target)
    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)

    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [activeStep, updateTargetRect, visible, welcomeVisible])

  useEffect(() => {
    if (!visible) {
      const resetTimer = window.setTimeout(() => setTargetRect(null), 0)
      return () => window.clearTimeout(resetTimer)
    }
  }, [visible])

  const startTourSteps = () => {
    const nextSteps = buildTourSteps(user)
    setTourSteps(nextSteps)
    setWelcomeVisible(false)
    setActiveStepIndex(0)
    runStepBefore(nextSteps[0])
    window.requestAnimationFrame(() => updateTargetRect(nextSteps[0].target))
  }

  const goPrevious = () => {
    if (welcomeVisible) return
    if (activeStepIndex === 0) {
      closePlatformDropdown()
      setWelcomeVisible(true)
      setTargetRect(null)
      return
    }

    const nextIndex = activeStepIndex - 1
    runStepBefore(tourSteps[nextIndex])
    setActiveStepIndex(nextIndex)
    window.setTimeout(() => updateTargetRect(tourSteps[nextIndex].target), 160)
  }

  const goNext = () => {
    if (welcomeVisible) {
      startTourSteps()
      return
    }

    if (activeStep.afterNext === 'finish-open-setup') {
      setVisible(false)
      closePlatformDropdown()
      closeSearchModal()
      openSetupGuide()
      return
    }

    if (activeStepIndex >= tourSteps.length - 1) return

    const nextIndex = activeStepIndex + 1
    runStepBefore(tourSteps[nextIndex])
    setActiveStepIndex(nextIndex)
    window.setTimeout(() => updateTargetRect(tourSteps[nextIndex].target), 200)
  }

  if (!visible) return null

  if (welcomeVisible) {
    return (
      <div className="demo-onboarding-tour demo-onboarding-tour--welcome" role="dialog" aria-modal="true" aria-labelledby="demo-onboarding-welcome-title">
        <div className="demo-onboarding-tour__welcome-card">
          <span className="demo-onboarding-tour__step">Başlangıç</span>
          <h2 id="demo-onboarding-welcome-title">Farktör paneline hoş geldiniz</h2>
          <p>
            Kısa bir turla panelin temel alanlarını birlikte gezeceğiz. İlk adımlar zorunlu;
            turu atlama seçeneğini daha sonra ekleyeceğiz.
          </p>
          <div className="demo-onboarding-tour__actions">
            <button type="button" onClick={startTourSteps}>
              Tura Başla
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!targetRect || !activeStep) return null

  const cardWidth = Math.min(TOUR_CARD_WIDTH, window.innerWidth - TOUR_MARGIN * 2)
  const spaceRight = window.innerWidth - (targetRect.left + targetRect.width) - TOUR_MARGIN
  const spaceLeft = targetRect.left - TOUR_MARGIN
  const spaceBottom = window.innerHeight - (targetRect.top + targetRect.height) - TOUR_MARGIN
  const spaceTop = targetRect.top - TOUR_MARGIN
  const placement = spaceRight >= cardWidth + TOUR_GAP
    ? 'right'
    : spaceTop >= TOUR_CARD_HEIGHT + TOUR_GAP
      ? 'top'
      : spaceLeft >= cardWidth + TOUR_GAP
        ? 'left'
        : spaceBottom >= TOUR_CARD_HEIGHT + TOUR_GAP
          ? 'bottom'
          : 'center'

  const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)
  const centeredTop = targetRect.top + targetRect.height / 2 - TOUR_CARD_HEIGHT / 2
  const centeredLeft = targetRect.left + targetRect.width / 2 - cardWidth / 2
  const tooltipTop = placement === 'top'
    ? targetRect.top - TOUR_CARD_HEIGHT - TOUR_GAP
    : placement === 'bottom'
      ? targetRect.top + targetRect.height + TOUR_GAP
      : clamp(centeredTop, TOUR_MARGIN, window.innerHeight - TOUR_CARD_HEIGHT - TOUR_MARGIN)
  const tooltipLeft = placement === 'right'
    ? targetRect.left + targetRect.width + TOUR_GAP
    : placement === 'left'
      ? targetRect.left - cardWidth - TOUR_GAP
      : clamp(centeredLeft, TOUR_MARGIN, window.innerWidth - cardWidth - TOUR_MARGIN)

  return (
    <div className="demo-onboarding-tour" role="dialog" aria-modal="true" aria-labelledby="demo-onboarding-title">
      <div
        className="demo-onboarding-tour__spotlight"
        style={{
          top: targetRect.top - 6,
          left: targetRect.left - 6,
          width: targetRect.width + 12,
          height: targetRect.height + 12,
        }}
      />
      <div
        className={`demo-onboarding-tour__card demo-onboarding-tour__card--${placement}`}
        style={{ top: tooltipTop, left: tooltipLeft, width: cardWidth }}
      >
        <span className="demo-onboarding-tour__step">{activeStepIndex + 1} / {tourSteps.length}</span>
        <h2 id="demo-onboarding-title">{activeStep.title}</h2>
        <p>{activeStep.body}</p>
        <div className="demo-onboarding-tour__actions">
          <button type="button" className="demo-onboarding-tour__secondary" onClick={goPrevious}>
            Önceki
          </button>
          <button type="button" onClick={goNext}>
            Sonraki
          </button>
        </div>
      </div>
    </div>
  )
}
